import { useCallback, useEffect, useState } from 'react';
import { baseUrl } from '../../main/client';
import type {
    CalendarBlockedDate,
    CalendarBooking,
    CalendarCleaning,
    CalendarEventsResponse,
    MissingCleaning,
} from '../types/calendar';

export interface BlockedDate {
    id: number;
    propertyId: number;
    startDate: string;
    endDate: string;
    blockType: 'maintenance' | 'personal';
    reason?: string;
    created: string;
    updated: string;
}

export interface CalendarDataResult {
    bookings: CalendarBooking[];
    blockedDates: CalendarBlockedDate[];
    cleanings: CalendarCleaning[];
    missingCleanings: MissingCleaning[];
    loading: boolean;
    error: string | null;
    reload: () => Promise<void>;
}

interface UseCalendarDataOptions {
    propertyId: number;
    month: number; // 1-12
    year: number;
    weekStartDay: 0 | 1; // 0 = Sunday, 1 = Monday
}

const useCalendarData = ({
    propertyId,
    month,
    year,
    weekStartDay,
}: UseCalendarDataOptions): CalendarDataResult => {
    const [bookings, setBookings] = useState<CalendarBooking[]>([]);
    const [blockedDates, setBlockedDates] = useState<CalendarBlockedDate[]>([]);
    const [cleanings, setCleanings] = useState<CalendarCleaning[]>([]);
    const [missingCleanings, setMissingCleanings] = useState<MissingCleaning[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadData = useCallback(async () => {
        if (!propertyId) {
            setBookings([]);
            setBlockedDates([]);
            setCleanings([]);
            setMissingCleanings([]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Calculate date range (visible month + buffer for multi-day events)
            const startDate = new Date(year, month - 1, 1);
            // Go back to the start of the week for the first day
            // getDay() returns 0-6 (Sun-Sat)
            const firstDayOfWeek = startDate.getDay();
            // For Sunday start (weekStartDay=0): go back firstDayOfWeek days
            // For Monday start (weekStartDay=1): go back (firstDayOfWeek + 6) % 7 days
            const daysToGoBack =
                weekStartDay === 0 ? firstDayOfWeek : (firstDayOfWeek + 6) % 7;
            startDate.setDate(startDate.getDate() - daysToGoBack - 7);

            const endDate = new Date(year, month, 0); // Last day of month
            // Go forward to the end of the week for the last day
            const lastDayOfWeek = endDate.getDay();
            // For Sunday start (weekStartDay=0): go forward (6 - lastDayOfWeek) days to Saturday
            // For Monday start (weekStartDay=1): go forward (7 - lastDayOfWeek) % 7 days to Sunday
            const daysToGoForward =
                weekStartDay === 0 ? 6 - lastDayOfWeek : (7 - lastDayOfWeek) % 7;
            endDate.setDate(endDate.getDate() + daysToGoForward + 7);

            const startDateStr = startDate.toISOString().split('T')[0];
            const endDateStr = endDate.toISOString().split('T')[0];

            const response = await fetch(
                `${baseUrl}/api/properties/${propertyId}/calendar-events?startDate=${startDateStr}&endDate=${endDateStr}`,
                {
                    headers: {
                        authorization: `bearer ${localStorage.getItem('token')}`,
                    },
                }
            );

            if (response.status === 401) {
                localStorage.removeItem('token');
                window.location.reload();
                throw new Error('Unauthorized');
            }

            if (!response.ok) {
                throw new Error('Failed to fetch calendar data');
            }

            const data: CalendarEventsResponse = await response.json();
            setBookings(data.bookings);
            setBlockedDates(data.blockedDates);
            setCleanings(data.cleanings ?? []);
            setMissingCleanings(data.missingCleanings ?? []);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : 'Failed to load calendar data'
            );
        } finally {
            setLoading(false);
        }
    }, [propertyId, month, year, weekStartDay]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    return {
        bookings,
        blockedDates,
        cleanings,
        missingCleanings,
        loading,
        error,
        reload: loadData,
    };
};

export default useCalendarData;
